// Filter Lab: compare engagement-floor filter presets for a single company.
// Side-by-side overlay of cached count series, with cost-aware backfill.

import { useEffect, useMemo, useState } from "react";
import { COLORS, fmtRaw, tStyles } from "../styles";
import { api, fmtMillsUsd, useHasAdminKey } from "../api";
import { aggregate, nowAlignedToHourSec } from "../aggregate";
import type {
  Company,
  CountBucket,
  FilterPreset,
  QuerySpec,
} from "../../shared/types";

const HOUR = 3600;
const SERIES_COLORS = ["#e8ff5c", "#7ef0a8", "#ff8888", "#88c8ff", "#ffb088"];

interface Props {
  companies: Array<Company & { specs: Array<QuerySpec & { filter_preset_name: string }> }>;
  presets: FilterPreset[];
  onBack: () => void;
  onPresetsChanged: () => void;
}

interface SeriesEntry {
  preset: FilterPreset;
  spec: QuerySpec | null;
  buckets: CountBucket[];
  loading: boolean;
}

export function Lab({ companies, presets, onBack, onPresetsChanged }: Props) {
  const hasAdmin = useHasAdminKey();
  const [companyId, setCompanyId] = useState<number | null>(
    companies[0]?.id ?? null,
  );
  const [activePresetIds, setActivePresetIds] = useState<Set<number>>(
    () => new Set(presets.filter((p) => p.is_default).map((p) => p.id)),
  );
  const [series, setSeries] = useState<Record<number, SeriesEntry>>({});
  const [windowDays, setWindowDays] = useState(7);
  const [editing, setEditing] = useState<FilterPreset | null>(null);

  const company = companies.find((c) => c.id === companyId) ?? null;

  // Load series for every active preset for the chosen company.
  useEffect(() => {
    if (!company) return;
    const end = nowAlignedToHourSec();
    const start = end - windowDays * 24 * HOUR;

    let cancelled = false;
    (async () => {
      const next: Record<number, SeriesEntry> = {};
      for (const presetId of activePresetIds) {
        const preset = presets.find((p) => p.id === presetId);
        if (!preset) continue;
        next[presetId] = {
          preset,
          spec: null,
          buckets: [],
          loading: true,
        };
      }
      if (!cancelled) setSeries({ ...next });

      for (const presetId of activePresetIds) {
        const preset = presets.find((p) => p.id === presetId);
        if (!preset) continue;
        try {
          const { spec } = await api.upsertSpec(company.id, preset.id);
          const r = await api.series(spec.query_hash, start, end + HOUR);
          if (cancelled) return;
          setSeries((s) => ({
            ...s,
            [presetId]: {
              preset,
              spec,
              buckets: r.buckets,
              loading: false,
            },
          }));
        } catch (e) {
          console.error("lab series", e);
          if (cancelled) return;
          setSeries((s) => ({
            ...s,
            [presetId]: { preset, spec: null, buckets: [], loading: false },
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company, activePresetIds, presets, windowDays]);

  return (
    <div style={tStyles.root}>
      <div style={tStyles.headerBar}>
        <div style={tStyles.brand}>
          <span style={tStyles.logoMark}>◼</span>
          <span style={tStyles.logoText}>MINDSHARE.X</span>
          <span style={tStyles.logoSlash}>::</span>
          <span style={tStyles.logoSub}>filter.lab</span>
        </div>
        <div style={tStyles.headerMeta}>
          <button style={tStyles.cmdLaunch} onClick={onBack}>
            ← back to dashboard
          </button>
        </div>
      </div>

      <div style={tStyles.toolbar}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={tStyles.segLabel}>COMPANY</span>
          <select
            value={companyId ?? ""}
            onChange={(e) => setCompanyId(Number(e.target.value))}
            style={{
              background: COLORS.bgSurface,
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              padding: "5px 9px",
              fontFamily: "inherit",
              fontSize: 11,
            }}
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div style={tStyles.controlsRight}>
          <div style={tStyles.segGroup}>
            <span style={tStyles.segLabel}>WIN</span>
            {[1, 7, 30].map((d) => (
              <button
                key={d}
                onClick={() => setWindowDays(d)}
                style={{
                  ...tStyles.seg,
                  ...(windowDays === d ? tStyles.segActive : {}),
                }}
              >
                {d}D
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 320px",
          overflow: "hidden",
        }}
      >
        {/* Chart */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRight: `1px solid ${COLORS.border}`,
          }}
        >
          <div style={tStyles.centerHeader}>
            <span style={tStyles.centerTitle}>
              {company?.name ?? "—"} · {Object.keys(series).length} preset(s) · last {windowDays}d hourly
            </span>
            <span style={tStyles.centerSub}>
              y = tweet_count · stroke colour = preset
            </span>
          </div>
          <div style={{ flex: 1, padding: "16px 20px", overflow: "auto" }}>
            <OverlayChart series={Object.values(series)} />
            <div style={{ marginTop: 16 }}>
              {Object.values(series).map((s, i) => {
                const colour = SERIES_COLORS[i % SERIES_COLORS.length];
                const agg = aggregate(s.buckets);
                return (
                  <div
                    key={s.preset.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "6px 0",
                      borderBottom: `1px dashed ${COLORS.border}`,
                      fontSize: 11,
                    }}
                  >
                    <span style={{ color: colour, width: 8 }}>■</span>
                    <span style={{ width: 110, color: COLORS.text }}>
                      {s.preset.name}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        color: COLORS.textDim,
                        fontFamily: "inherit",
                        fontSize: 10,
                      }}
                    >
                      {s.preset.filter_clause}
                    </span>
                    <span style={{ color: COLORS.textDim, width: 90, textAlign: "right" }}>
                      {fmtRaw(agg.vol7d)} /7d
                    </span>
                    <span
                      style={{
                        color:
                          agg.velocity24 === null
                            ? COLORS.textDim
                            : agg.velocity24 >= 0
                              ? COLORS.up
                              : COLORS.down,
                        width: 60,
                        textAlign: "right",
                      }}
                    >
                      {agg.velocity24 === null
                        ? "—"
                        : `${agg.velocity24 >= 0 ? "+" : ""}${agg.velocity24.toFixed(1)}%`}
                    </span>
                    {s.spec && (
                      <BackfillInline
                        queryHash={s.spec.query_hash}
                        days={windowDays}
                        onDone={onPresetsChanged}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right panel: preset list + editor */}
        <div style={{ ...tStyles.rail, borderRight: "none" }}>
          <div style={tStyles.railHeader}>
            <span style={tStyles.railTitleWatch}>FILTER PRESETS</span>
            <span style={tStyles.railCount}>{presets.length}</span>
          </div>
          <div style={tStyles.railList}>
            {presets.map((p) => {
              const active = activePresetIds.has(p.id);
              return (
                <div
                  key={p.id}
                  style={{
                    padding: "6px 6px",
                    borderBottom: `1px dashed ${COLORS.border}`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(e) => {
                        const next = new Set(activePresetIds);
                        if (e.target.checked) next.add(p.id);
                        else next.delete(p.id);
                        setActivePresetIds(next);
                      }}
                      style={{ accentColor: COLORS.accent }}
                    />
                    <span style={{ flex: 1, color: COLORS.text, fontSize: 11 }}>
                      {p.name}
                      {p.is_default ? (
                        <span style={{ color: COLORS.accent, marginLeft: 6, fontSize: 9 }}>
                          DEFAULT
                        </span>
                      ) : null}
                    </span>
                    {hasAdmin && (
                      <button
                        onClick={() =>
                          setEditing({
                            id: 0,
                            name: `${p.name} v2`,
                            filter_clause: p.filter_clause,
                            is_default: 0,
                            created_at: 0,
                          })
                        }
                        title="presets are immutable — duplicates with this clause as starting point"
                        style={{
                          background: "transparent",
                          border: "none",
                          color: COLORS.textDim,
                          cursor: "pointer",
                          fontSize: 10,
                        }}
                      >
                        duplicate
                      </button>
                    )}
                  </div>
                  <div
                    style={{
                      color: COLORS.textDim,
                      fontSize: 10,
                      marginLeft: 22,
                      marginTop: 2,
                    }}
                  >
                    {p.filter_clause}
                  </div>
                </div>
              );
            })}
            {hasAdmin && (
              <button
                onClick={() => setEditing({ id: 0, name: "", filter_clause: "", is_default: 0, created_at: 0 })}
                style={{
                  ...tStyles.drillButton,
                  marginTop: 8,
                  width: "100%",
                  marginLeft: 0,
                }}
              >
                + new preset
              </button>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <PresetEditor
          preset={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onPresetsChanged();
          }}
        />
      )}
    </div>
  );
}

function OverlayChart({ series }: { series: SeriesEntry[] }) {
  const W = 880;
  const H = 220;

  const allPoints = useMemo(() => {
    const all = series.flatMap((s) => s.buckets.map((b) => b.tweet_count));
    return all;
  }, [series]);
  const yMax = Math.max(1, ...allPoints);

  const xMin = useMemo(() => {
    const all = series.flatMap((s) => s.buckets.map((b) => b.bucket_start));
    return all.length > 0 ? Math.min(...all) : 0;
  }, [series]);
  const xMax = useMemo(() => {
    const all = series.flatMap((s) => s.buckets.map((b) => b.bucket_start));
    return all.length > 0 ? Math.max(...all) : 1;
  }, [series]);
  const xRange = Math.max(1, xMax - xMin);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: "100%", height: H, background: COLORS.bgSurface, border: `1px solid ${COLORS.border}` }}
    >
      {[0.25, 0.5, 0.75].map((g) => (
        <line
          key={g}
          x1="0"
          x2={W}
          y1={H - g * (H - 20) - 10}
          y2={H - g * (H - 20) - 10}
          stroke={COLORS.border}
          strokeDasharray="2 4"
        />
      ))}
      {series.map((s, idx) => {
        if (s.buckets.length === 0) return null;
        const colour = SERIES_COLORS[idx % SERIES_COLORS.length];
        const sorted = [...s.buckets].sort(
          (a, b) => a.bucket_start - b.bucket_start,
        );
        const pts = sorted
          .map((b) => {
            const x = ((b.bucket_start - xMin) / xRange) * W;
            const y = H - (b.tweet_count / yMax) * (H - 20) - 10;
            return `${x},${y}`;
          })
          .join(" ");
        return (
          <polyline
            key={s.preset.id}
            points={pts}
            fill="none"
            stroke={colour}
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        );
      })}
      <text x={4} y={14} fill={COLORS.textDim} fontSize="9" fontFamily="inherit">
        max: {fmtRaw(yMax)}
      </text>
    </svg>
  );
}

function BackfillInline({
  queryHash,
  days,
  onDone,
}: {
  queryHash: string;
  days: number;
  onDone: () => void;
}) {
  const hasAdmin = useHasAdminKey();
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "previewing" }
    | { kind: "preview"; mills: number; calls: number; gaps: number }
    | { kind: "running" }
    | { kind: "done"; mills: number }
    | { kind: "error"; msg: string }
  >({ kind: "idle" });

  const click = async () => {
    const end = nowAlignedToHourSec();
    const start = end - days * 24 * HOUR;
    if (state.kind === "idle" || state.kind === "done" || state.kind === "error") {
      setState({ kind: "previewing" });
      try {
        const cost = await api.gapCost(queryHash, start, end + HOUR);
        if (cost.gaps === 0) {
          setState({ kind: "done", mills: 0 });
          return;
        }
        setState({
          kind: "preview",
          mills: cost.cost_mills,
          calls: cost.api_calls,
          gaps: cost.gaps,
        });
      } catch (e) {
        setState({ kind: "error", msg: e instanceof Error ? e.message : String(e) });
      }
      return;
    }
    if (state.kind === "preview") {
      setState({ kind: "running" });
      try {
        const r = await api.sync(queryHash, start, end + HOUR);
        if (r.errors.length > 0) {
          setState({ kind: "error", msg: r.errors[0] ?? "unknown" });
        } else {
          setState({ kind: "done", mills: r.cost_mills });
          onDone();
        }
      } catch (e) {
        setState({ kind: "error", msg: e instanceof Error ? e.message : String(e) });
      }
    }
  };

  if (!hasAdmin) return null;

  let label: string;
  if (state.kind === "idle") label = "fill";
  else if (state.kind === "previewing") label = "…";
  else if (state.kind === "preview")
    label = `${fmtMillsUsd(state.mills)} · ${state.gaps} gaps`;
  else if (state.kind === "running") label = "fetching…";
  else if (state.kind === "done")
    label = state.mills === 0 ? "✓ full" : `✓ ${fmtMillsUsd(state.mills)}`;
  else label = `✗`;

  return (
    <button
      onClick={click}
      title={state.kind === "error" ? state.msg : ""}
      style={{
        background: "transparent",
        border: `1px solid ${COLORS.border}`,
        color: COLORS.accent,
        padding: "2px 8px",
        fontFamily: "inherit",
        fontSize: 10,
        cursor: "pointer",
        letterSpacing: 0.5,
        width: 110,
      }}
    >
      {label}
    </button>
  );
}

function PresetEditor({
  preset,
  onClose,
  onSaved,
}: {
  preset: FilterPreset;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Presets are immutable — this editor only ever creates new ones. The
  // "duplicate" button on existing presets opens this with the clause
  // prefilled but a fresh name.
  const [name, setName] = useState(preset.name);
  const [clause, setClause] = useState(preset.filter_clause);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api.createFilterPreset(name, clause);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  return (
    <div style={tStyles.cmdBackdrop} onClick={onClose}>
      <div
        style={{ ...tStyles.cmdPanel, width: 560 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={tStyles.cmdInputRow}>
          <span style={tStyles.cmdPrompt}>+</span>
          <span style={{ ...tStyles.cmdInput, flex: 1 }}>new preset</span>
          <span style={tStyles.cmdHint}>ESC to close</span>
        </div>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ color: COLORS.textDim, fontSize: 10, letterSpacing: 1 }}>NAME</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              background: COLORS.bg,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.text,
              padding: "6px 8px",
              fontFamily: "inherit",
              fontSize: 12,
            }}
          />
          <label style={{ color: COLORS.textDim, fontSize: 10, letterSpacing: 1 }}>FILTER CLAUSE</label>
          <textarea
            value={clause}
            onChange={(e) => setClause(e.target.value)}
            rows={4}
            style={{
              background: COLORS.bg,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.text,
              padding: "6px 8px",
              fontFamily: "inherit",
              fontSize: 12,
              resize: "vertical",
            }}
          />
          <div style={{ color: COLORS.textDim, fontSize: 10, lineHeight: 1.5 }}>
            Will be appended to each company's <code>main_query</code>. Presets
            are immutable once created — to iterate, save under a new name. Old
            data under the original preset stays cached and queryable.
          </div>
          {err && <div style={{ color: COLORS.down, fontSize: 11 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={onClose}
              style={{ ...tStyles.cmdLaunch }}
              disabled={saving}
            >
              cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              style={{ ...tStyles.drillButton, marginTop: 0 }}
            >
              {saving ? "saving…" : "create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
